import React, { useCallback } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";
import { getLiveViewUnavailableMessage } from "../LiveStreamPlayer";
import { VIDEO_FRAME_CLASS } from "./siteVideoWallConstants";
import { useWallStreams } from "./WallStreams";

const theme = createTheme({
  palette: {
    primary: {
      main: "#FF8400",
    },
  },
});

interface SiteVideoWallTileProps {
  cameraId: string;
  cameraName: string;
  enabled: boolean;
  isFocused?: boolean;
  onClick?: () => void;
  className?: string;
  compact?: boolean;
  showControls?: boolean;
  placeholderMessage?: string;
  fitToContainer?: boolean;
}

/**
 * A wall tile. The <video> + player are NOT owned here — they live in
 * WallStreamsProvider and are moved (appendChild) into this tile's slot while
 * this tile is the one displaying the camera (see `slotRef`). This tile only
 * renders the frame, LIVE badge, loading/unavailable overlay, placeholder, and
 * camera name, reading status from the registry — so the player survives this
 * tile unmounting/remounting on a layout/mode change.
 */
export const SiteVideoWallTile: React.FC<SiteVideoWallTileProps> = ({
  cameraId,
  cameraName,
  enabled,
  isFocused = false,
  onClick,
  className = "",
  compact = true,
  // showControls is accepted for API compatibility but no longer used: the
  // <video> is owned by WallStreamsProvider, so controls aren't attached here.
  placeholderMessage,
  fitToContainer = false,
}) => {
  const { registerSlot, useStreamStatus } = useWallStreams();
  const status = useStreamStatus(cameraId);

  // This tile actually displays the camera (vs a placeholder slot — e.g. the
  // focused camera's spot in the side grid, which reads "Displaying video on
  // left" while the video shows in the spotlight tile).
  const streamHere = enabled && !placeholderMessage;

  // Claim the persistent <video> into our slot while we're the displaying tile;
  // release it (→ holder / another slot) on unmount or when we become a
  // placeholder. The player is NOT torn down by this register/unregister.
  const slotRef = useCallback(
    (node: HTMLDivElement | null) => {
      registerSlot(cameraId, streamHere ? node : null);
    },
    [registerSlot, cameraId, streamHere]
  );

  const showVideo = streamHere && status === "playing";
  const showLoading =
    streamHere &&
    (status === "loading" || status === "idle" || status === "starting");
  const showUnavailable =
    streamHere &&
    (status === "unavailable" ||
      status === "camera_offline" ||
      status === "quota_reached" ||
      status === "session_expired" ||
      status === "codec_unsupported");
  const showIdlePaused = streamHere && status === "idle_paused";
  const showPlaceholder = Boolean(placeholderMessage);

  const frameRing = isFocused
    ? "border-actuate-orange"
    : onClick
      ? "border-transparent hover:border-actuate-orange"
      : "border-transparent";

  const frameClassName = fitToContainer
    ? `relative box-border aspect-[4/3] max-h-full w-full max-w-full ${VIDEO_FRAME_CLASS} ${frameRing}`
    : `relative box-border aspect-[4/3] w-full ${VIDEO_FRAME_CLASS} ${frameRing}`;

  const cameraNameClass = compact
    ? "pointer-events-none absolute left-1 top-1 z-10 max-w-[calc(100%-8px)] truncate rounded bg-black/55 px-1.5 py-0.5 font-mulish text-[10px] text-white"
    : "pointer-events-none absolute left-3 top-3 z-10 max-w-[calc(100%-24px)] truncate rounded bg-black/55 px-3 py-1.5 font-mulish text-[20px] text-white";

  const unavailableMessageClass = compact
    ? "font-mulish text-center text-[9px] leading-tight text-white/80"
    : "font-mulish px-4 text-center text-xl font-semibold leading-snug text-white/90";

  const liveBadgeClass = compact
    ? "pointer-events-none absolute right-1 top-1 z-10 inline-flex items-center gap-1 rounded bg-[#D93636] px-1.5 py-0.5 font-mulish text-[8px] font-bold uppercase tracking-[0.08em] text-white"
    : "pointer-events-none absolute right-3 top-3 z-10 inline-flex items-center gap-2 rounded bg-[#D93636] px-3 py-1.5 font-mulish text-[14px] font-bold uppercase tracking-[0.08em] text-white shadow-[0_2px_8px_rgba(217,54,54,0.4)]";

  const liveDotClass = compact
    ? "h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-white"
    : "h-2.5 w-2.5 shrink-0 animate-pulse rounded-full bg-white";

  return (
    <div
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={
        fitToContainer
          ? `flex h-full w-full min-h-0 min-w-0 items-center justify-center ${onClick ? "cursor-pointer" : ""} ${className}`
          : `w-full min-w-0 self-start ${onClick ? "cursor-pointer" : ""} ${className}`
      }
      data-testid={`wall-tile-${cameraId}`}
      data-enabled={String(streamHere)}
    >
      <div className={frameClassName}>
        {/* Slot the persistent <video> is moved into. Always rendered/visible
            while this tile displays the camera; the loading/unavailable overlay
            below renders on top until the stream is playing. (We don't
            display:none it — parking a live <video> in a hidden container can
            stall playback.) */}
        {streamHere && (
          <div
            ref={slotRef}
            className="pointer-events-none absolute inset-0 overflow-hidden rounded"
          />
        )}
        {showVideo && (
          <div className={liveBadgeClass} aria-label="Live stream">
            <span className={liveDotClass} />
            <span>LIVE</span>
          </div>
        )}
        {(showLoading || showUnavailable) && !showPlaceholder && (
          <div
            className={`pointer-events-none absolute inset-0 flex flex-col items-center justify-center bg-black ${
              compact ? "px-1" : "px-4"
            }`}
          >
            {showLoading ? (
              <>
                <ThemeProvider theme={theme}>
                  <CircularProgress color="primary" size={compact ? 18 : 32} />
                </ThemeProvider>
                {compact ? null : (
                  <p className="font-mulish mt-2 text-center text-[12px] text-white">
                    Connecting...
                  </p>
                )}
              </>
            ) : (
              <p className={unavailableMessageClass}>
                {getLiveViewUnavailableMessage(status)}
              </p>
            )}
          </div>
        )}
        {showIdlePaused && !showPlaceholder && (
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black px-2"
            data-testid={`wall-tile-paused-${cameraId}`}
          >
            <p
              className={
                compact
                  ? "font-mulish text-center text-[9px] text-white/70"
                  : "font-mulish text-center text-lg font-semibold text-white/80"
              }
            >
              Paused
            </p>
          </div>
        )}
        {showPlaceholder && (
          <div className="absolute inset-0 flex items-center justify-center bg-black px-2">
            <p className="font-mulish text-center text-[10px] font-semibold leading-snug text-actuate-orange">
              {placeholderMessage}
            </p>
          </div>
        )}
        <span className={cameraNameClass}>{cameraName}</span>
      </div>
    </div>
  );
};
